import React from 'react';
import './BlogPost.css';
import SEOHead from '../../components/SEOHead';
import FAQSchema from '../../components/FAQSchema';

const BrakeMaintenancePage: React.FC = () => {
  const faqs = [
    {
      question: "How often should I service my trailer brakes?",
      answer: "Inspect trailer brakes every 3,000 miles or annually, whichever comes first. For saltwater use, inspect every 1,000 miles or after each saltwater trip. Replace brake shoes when they reach 3/32 inch thickness or every 12,000 miles. Electric brake magnets should be checked annually and replaced when resistance falls outside 3-4 ohms."
    },
    {
      question: "What's the difference between electric and surge brakes?",
      answer: "Electric brakes use an electronic brake controller in the tow vehicle to activate electromagnets in the trailer's brake assemblies. Surge brakes are hydraulic and activate automatically when the trailer pushes against the tow vehicle during deceleration. Electric brakes offer better control and adjustability, while surge brakes are simpler and don't require a brake controller."
    },
    {
      question: "How do I know if my trailer brakes need adjustment?",
      answer: "Signs that brakes need adjustment include: stopping distance has increased noticeably, trailer pushes the tow vehicle when braking, brakes grab unevenly causing trailer to pull to one side, brake controller shows low power draw (electric brakes), or you can spin the wheel freely by hand when brakes should be holding. Most brakes need adjustment every 3,000-5,000 miles."
    },
    {
      question: "Can I use my trailer with failed brakes?",
      answer: "It depends on your location's laws and the trailer's weight. Most states require brakes on trailers over 3,000 lbs GVWR. Even if legal, towing without working brakes is extremely dangerous and increases stopping distance significantly. If brakes fail, have them repaired immediately before towing, or arrange for professional trailer transport."
    }
  ];

  return (
    <div className="blog-post">
      <SEOHead
        title="Trailer Brake Maintenance Guide | When to Service Your Brakes"
        description="Complete guide to boat trailer brake maintenance. Learn when to service electric and surge brakes, warning signs of brake problems, and maintenance schedules."
        keywords="trailer brakes, brake maintenance, electric brakes, surge brakes, brake service, trailer brake repair, brake inspection"
        canonical="https://fastboattrailers.com/tips/brake-maintenance-guide"
      />
      <FAQSchema faqs={faqs} />
      <div className="container">
        <article className="blog-article">
          <header className="blog-header">
            <h1>Trailer Brake Maintenance Guide</h1>
            <div className="blog-meta">
              <span className="blog-read-time">6 min read</span>
            </div>
          </header>

          <div className="blog-content">
            <p className="blog-intro">
              Trailer brakes are critical for safe towing, yet they're often neglected until they fail. Whether you have electric
              or surge brakes, regular maintenance keeps you safe and prevents costly repairs.
            </p>

            <h2>Understanding Trailer Brake Types</h2>

            <h3>Electric Brakes</h3>
            <p>
              Most common on modern trailers, electric brakes use an electromagnet to activate brake shoes:
            </p>
            <ul>
              <li>Controlled by a brake controller in your tow vehicle</li>
              <li>Proportional braking - harder you brake, harder trailer brakes</li>
              <li>Require working trailer wiring connection</li>
              <li>Need periodic adjustment as shoes wear</li>
            </ul>

            <h3>Surge Brakes</h3>
            <p>
              Hydraulic brakes that activate when trailer pushes forward on tow vehicle:
            </p>
            <ul>
              <li>No brake controller needed</li>
              <li>Self-contained hydraulic system in trailer coupler</li>
              <li>Activate automatically when you slow down</li>
              <li>Must be locked out when backing up</li>
            </ul>

            <h2>Maintenance Schedule</h2>

            <h3>Every Trip (Pre-Trip Inspection)</h3>
            <ul>
              <li>Test brakes before leaving - pull forward and brake to ensure they engage</li>
              <li>Check brake controller is functioning (for electric brakes)</li>
              <li>Verify brake lights are working</li>
              <li>Listen for unusual noises when braking</li>
            </ul>

            <h3>Monthly or Every 3,000 Miles</h3>
            <ul>
              <li>Inspect brake assemblies through backing plate holes</li>
              <li>Check for brake dust or excessive wear</li>
              <li>Test adjustment - brakes should engage smoothly</li>
              <li>Inspect wiring connections for corrosion (electric brakes)</li>
            </ul>

            <h3>Every 6 Months or 10,000 Miles</h3>
            <ul>
              <li>Remove drums and inspect brake shoes</li>
              <li>Measure shoe thickness - replace if less than 1/8 inch</li>
              <li>Inspect drums for scoring, cracks, or out-of-round</li>
              <li>Clean and adjust brakes</li>
              <li>Check magnet operation (electric brakes)</li>
            </ul>

            <h3>Annually</h3>
            <ul>
              <li>Complete brake service including cleaning and lubrication</li>
              <li>Replace brake fluid (surge brakes)</li>
              <li>Inspect and replace worn hardware</li>
              <li>Check all brake wiring and connections</li>
              <li>Test brake controller settings and output</li>
            </ul>

            <h2>Warning Signs You Need Brake Service</h2>
            <p>
              Don't ignore these symptoms:
            </p>

            <h3>Electric Brakes</h3>
            <ul>
              <li><strong>Squealing or grinding</strong> - Worn shoes or contaminated drums</li>
              <li><strong>No braking power</strong> - Bad controller, wiring issue, or worn magnets</li>
              <li><strong>Brakes dragging</strong> - Over-adjusted or stuck shoes</li>
              <li><strong>One wheel hotter than others</strong> - That brake is doing all the work</li>
              <li><strong>Brake controller error</strong> - Short circuit or open connection</li>
              <li><strong>Pulling to one side</strong> - Uneven brake adjustment or failed brake</li>
            </ul>

            <h3>Surge Brakes</h3>
            <ul>
              <li><strong>Soft brake pedal</strong> - Air in hydraulic system</li>
              <li><strong>Brakes won't release</strong> - Stuck master cylinder or return spring failure</li>
              <li><strong>Hard pedal, no braking</strong> - Worn shoes or bad master cylinder</li>
              <li><strong>Brake fluid leaks</strong> - Failed seals or damaged lines</li>
              <li><strong>Trailer pushes vehicle forward</strong> - Brakes not engaging properly</li>
            </ul>

            <h2>How to Adjust Electric Brakes</h2>
            <p>
              Proper adjustment is critical for safe braking:
            </p>
            <ol>
              <li><strong>Jack up wheel</strong> so it spins freely</li>
              <li><strong>Locate adjuster</strong> - star wheel accessible through backing plate slot</li>
              <li><strong>Rotate adjuster</strong> with brake spoon or screwdriver</li>
              <li><strong>Tighten until wheel won't turn</strong></li>
              <li><strong>Back off 10-12 clicks</strong> until wheel spins freely with slight drag</li>
              <li><strong>Repeat for all wheels</strong></li>
              <li><strong>Test drive</strong> and verify even braking</li>
            </ol>

            <h2>Brake Service Procedures</h2>

            <h3>Cleaning Brake Assemblies</h3>
            <p>
              Especially important after saltwater exposure:
            </p>
            <ul>
              <li>Remove wheels and drums</li>
              <li>Use brake cleaner spray - never compressed air (asbestos risk on old brakes)</li>
              <li>Wipe down all surfaces</li>
              <li>Inspect for damage, wear, or corrosion</li>
              <li>Clean drum with brake cleaner and inspect for scoring</li>
            </ul>

            <h3>Replacing Brake Shoes</h3>
            <p>
              Replace when linings are less than 1/8 inch thick:
            </p>
            <ul>
              <li>Take photos before disassembly for reference</li>
              <li>Remove hold-down springs and pins</li>
              <li>Disconnect shoes from brake lever and adjuster</li>
              <li>Install new shoes in reverse order</li>
              <li>Replace all springs and hardware</li>
              <li>Adjust and test</li>
            </ul>

            <h3>Magnet Inspection (Electric Brakes)</h3>
            <p>
              Magnets wear over time and need replacement:
            </p>
            <ul>
              <li>Check magnet face for wear, cracks, or damage</li>
              <li>Test magnet continuity with multimeter (should read 3-4 ohms)</li>
              <li>Inspect magnet wiring for breaks or shorts</li>
              <li>Replace magnets in pairs (both wheels on an axle)</li>
            </ul>

            <h2>Brake Controller Setup</h2>
            <p>
              For electric brakes, proper controller setup is essential:
            </p>

            <h3>Initial Setup</h3>
            <ul>
              <li>Set gain/output to maximum</li>
              <li>Make a test stop from 25 mph on level pavement</li>
              <li>Reduce gain until wheels don't lock up but trailer brakes hard</li>
              <li>Fine-tune so vehicle and trailer brake together smoothly</li>
            </ul>

            <h3>Adjustment for Conditions</h3>
            <ul>
              <li>Increase gain when loaded heavy</li>
              <li>Decrease for lighter loads</li>
              <li>Adjust for rain (less gain to prevent skidding)</li>
              <li>Increase for steep downhill grades</li>
            </ul>

            <h2>Saltwater Considerations</h2>
            <p>
              Brakes are especially vulnerable to saltwater damage:
            </p>
            <ul>
              <li>Rinse brake assemblies thoroughly after every saltwater use</li>
              <li>Remove wheels and spray brake cleaner into assemblies</li>
              <li>Consider stainless steel hardware in saltwater environments</li>
              <li>Apply anti-seize to brake adjuster threads</li>
              <li>Inspect more frequently - monthly instead of quarterly</li>
              <li>Some boaters skip brakes on saltwater trailers and use larger vehicles</li>
            </ul>

            <h2>Drum Replacement</h2>
            <p>
              Replace drums if:
            </p>
            <ul>
              <li>Scored or grooved more than 0.060 inches deep</li>
              <li>Out-of-round (wobbles when spinning)</li>
              <li>Cracked or damaged</li>
              <li>Maximum diameter has been exceeded (check drum for specs)</li>
              <li>Turned more than twice (drums can only be resurfaced limited times)</li>
            </ul>

            <h2>DIY vs. Professional Service</h2>

            <h3>DIY Friendly:</h3>
            <ul>
              <li>Brake adjustment</li>
              <li>Visual inspection</li>
              <li>Cleaning</li>
              <li>Brake controller setup</li>
            </ul>

            <h3>Consider Professional:</h3>
            <ul>
              <li>Complete brake replacement</li>
              <li>Drum turning or replacement</li>
              <li>Hydraulic system service (surge brakes)</li>
              <li>Electrical diagnosis and repair</li>
              <li>If you're not mechanically inclined</li>
            </ul>

            <div className="blog-callout">
              <h3>Safety First</h3>
              <p>
                Brakes are a critical safety system. If you're not confident in your ability to service them properly,
                have a professional do the work. The cost of professional service is far less than the cost of an accident
                caused by brake failure.
              </p>
            </div>

            <h2>Keep Records</h2>
            <p>
              Document all brake maintenance:
            </p>
            <ul>
              <li>Date and mileage of all service</li>
              <li>What was done (inspection, adjustment, replacement)</li>
              <li>Parts replaced and measurements taken</li>
              <li>Next service due date</li>
              <li>Problems found and corrected</li>
            </ul>

            <p>
              Regular brake maintenance is cheap insurance. The time and money spent keeping your trailer brakes in top
              condition pales in comparison to the consequences of brake failure while towing. Make brake service a priority!
            </p>
          </div>
        </article>
      </div>
    </div>
  );
};

export default BrakeMaintenancePage;
